import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { forkJoin, of, timer, Subscription, type Observable } from 'rxjs';
import { catchError, map, switchMap, take, takeWhile } from 'rxjs/operators';
import {
  DataForSEOService,
  UrlAnalysisResponse,
  SectionCoverageRow,
  SupportingPagesV2RowResult
} from '../../services/dataforseo';
import {
  PageComparisonService,
  PageComparisonResult,
  AnalysisStatusResponse,
  IncomingLink,
  DomainTopic
} from '../../services/page-comparison';
import { DomainTopicsService } from '../../services/domain-topics';
import {
  computeTopicFitData,
  emptyTopicFitData,
  extractDomainFromUrl,
  formatAnchorText,
  formatTopicTier,
  getLinkTypeLabel,
  getLinkTypeTooltip,
  getMainTopicFromAnalysis,
  getTopicSignalsCount,
  linkTargetDisplayUrl,
  linkTypeBadgeClass,
  QuickCompareTopicFitData,
  resolveUrlIdFromAnalysis,
  topicTierBadgeClass
} from '../../utils/quick-compare-topic-fit';
import {
  buildQuickCompareDiagnosisBullets,
  computeKeywordOverlapFromAnalyses,
  diagnosisHasCompetitorWins,
  diagnosisHasYourWins,
  getAnalyzedAtIso,
  getContentOpportunitiesFromOverlap,
  getCriticalIssues,
  getLastModifiedIso,
  getLinkStatsFromAnalysis,
  type ContentOpportunity,
  type DiagnosisBullet,
  type KeywordOverlapAnalysis,
  rankedKeywordsNeverFetched,
  rankedKeywordsFetchedEmpty,
  rankedKeywordsDevDiagnostic
} from '../../utils/quick-compare-analysis';
import {
  buildSectionGapVerdict,
  rankDrivenGapsForV2Row,
  type RankDrivenSectionGap,
  type SectionGapVerdict
} from '../../utils/rank-driven-section-gaps';
import {
  sectionGapClosestHeading,
  sectionGapClosestPct,
  sectionGapHasCloseMatch,
  sectionGaps
} from '../../utils/section-gaps';
import { splitPassageByKeywordTokens, rankDrivenHeadingTag } from '../../utils/keyword-passage-highlight';
import {
  buildTopicFitSupportingPagesInsight,
  type TopicFitSupportingPagesTopic
} from '../../utils/topic-fit-supporting-pages-insight';
import { normalizeUrlForCompare } from '../../utils/topic-supporting-pages';
import {
  loadSupportingPagesContext,
  type SupportingPagesPresenterTopic
} from '../../utils/supporting-pages-presenter';
import { FeatureFlagService } from '../../services/feature-flag';

type ColumnSide = 'primary' | 'compare';

interface ColumnState {
  status: 'loading' | 'analyzing' | 'completed' | 'error' | 'empty' | 'prompt';
  analysis: any | null;
  error: string | null;
  progress: { pagesCrawled: number; pagesInQueue: number } | null;
  /**
   * Why this column is waiting to be asked. Two different situations now reach
   * `prompt` and they cost different things: `not_analyzed` needs a crawl and
   * a keyword fetch, `no_keywords` already has the page and needs only Labs.
   * Telling an operator "this URL hasn't been analyzed yet" about a page we
   * demonstrably hold an analysis of is the kind of small lie that makes the
   * rest of the panel suspect.
   */
  promptReason?: 'not_analyzed' | 'no_keywords';
}

interface IncomingLinksColumnState {
  metadata: {
    totalIncomingLinks: number;
    contextualLinks: number;
    structuralLinks: number;
    samePageLinks: number;
    unknownLinks: number;
    requiresDomainCrawl: boolean;
    pagesAnalyzedInDomain?: number;
    /** 0 means nothing could have linked here — see `incomingLinksUnmeasured`. */
    linkSourcePagesInDomain?: number;
    domain: string;
  } | null;
  links: IncomingLink[] | null;
  fullLoading: boolean;
  error: string | null;
  expanded: boolean;
  showAll: boolean;
}

interface SupportingTopic extends SupportingPagesPresenterTopic {}

interface SupportingPagesState {
  status: 'idle' | 'loading' | 'ready' | 'error';
  topics: SupportingTopic[];
  expanded: boolean;
  source?: 'snapshot' | 'live' | 'empty';
}

/**
 * Section coverage of the compare page vs your whole page ("what they cover that
 * your page doesn't"). Computed pairwise by the content-gap sidecar via the
 * supporting-pages opportunity eval — no domain crawl needed, just the two
 * on-demand analyses this drawer already loads.
 */
interface SectionGapState {
  status: 'idle' | 'loading' | 'ready' | 'unavailable' | 'error';
  /** True when the content-gap sidecar was reachable. */
  serviceAvailable: boolean;
  missingSectionCount: number;
  rows: SectionCoverageRow[];
  /** Set when the gap was skipped (e.g. a page had too little body text). */
  skippedNote: string | null;
  /** Set when bodies exceeded the sidecar limit and were truncated before analysis. */
  warningNote: string | null;
  error: string | null;
  expanded: boolean;
  rankDrivenGaps: RankDrivenSectionGap[];
  cachedEvalRow: SupportingPagesV2RowResult | null;
}

@Component({
  selector: 'app-inventory-quick-compare-drawer',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './inventory-quick-compare-drawer.html',
  styleUrl: './inventory-quick-compare-drawer.css'
})
export class InventoryQuickCompareDrawerComponent implements OnChanges, OnDestroy {
  @Input() open = false;
  @Input() primaryUrl: string | null = null;
  @Input() compareUrl: string | null = null;
  @Input() primaryDomainLabel = '';
  @Input() compareDomainLabel = '';

  @Output() closed = new EventEmitter<void>();
  /** User chose to open Full Compare (Topic Share pipeline) on `/page-comparison`; parent navigates with URLs. */
  @Output() openFullCompare = new EventEmitter<void>();

  primaryColumn: ColumnState = this.initialColumn();
  compareColumn: ColumnState = this.initialColumn();

  primaryIncoming: IncomingLinksColumnState = this.initialIncoming();
  compareIncoming: IncomingLinksColumnState = this.initialIncoming();

  /** Synced: expand/collapse critical issues details (both columns). */
  issuesDetailsExpanded = false;
  keywordOpportunitiesExpanded = false;
  keywordDetailsExpanded = false;

  readonly internalLinksDisplayLimit = 25;

  /** Shown when Labs ranked keywords are still absent after on-page analysis loaded. */
  readonly keywordFetchHint =
    'Labs ranked keywords are not stored for this page yet. Quick Compare fetches them with on-page analysis; refresh if overlap stays empty.';

  readonly keywordFetchedEmptyHint =
    'Labs ranked-keywords fetch returned 0 for this URL (common for government or thin pages). If Setup → Rival ranked keywords listed a fetch failure for this URL, re-run that step; otherwise this page may genuinely rank for nothing Labs reports.';

  readonly keywordPartialHint =
    'Keyword overlap, opportunities, rank-driven section gaps, and some competitive signals need Labs ranked-keyword data on both pages.';

  private domainTopicsCache = new Map<string, DomainTopic[]>();
  private domainTopicsLoading = new Map<string, boolean>();
  private domainTopicsError = new Map<string, string | null>();

  supportingPages: SupportingPagesState = { status: 'idle', topics: [], expanded: false };
  sectionGap: SectionGapState = this.initialSectionGap();

  private loadRequestGen = 0;
  private loadSubscription = new Subscription();
  private priorBodyOverflow = '';

  constructor(
    private dataForSeo: DataForSEOService,
    private pageComparison: PageComparisonService,
    private domainTopicsService: DomainTopicsService,
    private featureFlags: FeatureFlagService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['open']) {
      if (this.open) {
        this.priorBodyOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        this.startLoad();
      } else {
        document.body.style.overflow = this.priorBodyOverflow;
        this.teardownLoads();
      }
    } else if (this.open && (changes['primaryUrl'] || changes['compareUrl'])) {
      this.startLoad();
    }
  }

  ngOnDestroy(): void {
    this.teardownLoads();
    if (this.open) {
      document.body.style.overflow = this.priorBodyOverflow;
    }
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent): void {
    if (!this.open || event.key !== 'Escape') return;
    event.preventDefault();
    this.emitClose();
  }

  emitClose(): void {
    this.closed.emit();
  }

  onOpenFullCompareClick(): void {
    const p = (this.primaryUrl || '').trim();
    const c = (this.compareUrl || '').trim();
    if (!p || !c) return;
    this.openFullCompare.emit();
  }

  get canOpenFullCompare(): boolean {
    return !!(this.primaryUrl || '').trim() && !!(this.compareUrl || '').trim();
  }

  pageTitle(analysis: any): string {
    return (
      analysis?.rawData?.pageData?.meta?.title ||
      analysis?.rawData?.summary?.title ||
      '—'
    );
  }

  rankedKeywordDisplayCount(analysis: any): string {
    const n = analysis?.rankedKeywords?.keywords?.length;
    const total = analysis?.rankedKeywords?.totalCount;
    if (typeof n === 'number' && n > 0) return String(n);
    if (typeof total === 'number' && total > 0) return String(total);
    if (rankedKeywordsFetchedEmpty(analysis)) return '0 (Labs)';
    return '—';
  }

  rankedKeywordsMissingNote(analysis: any): boolean {
    return rankedKeywordsNeverFetched(analysis);
  }

  rankedKeywordsNoteForAnalysis(analysis: any): string | null {
    if (rankedKeywordsNeverFetched(analysis)) return this.keywordFetchHint;
    if (rankedKeywordsFetchedEmpty(analysis)) return this.keywordFetchedEmptyHint;
    return null;
  }

  rankedKeywordsNoteVisible(analysis: any): boolean {
    return rankedKeywordsNeverFetched(analysis) || rankedKeywordsFetchedEmpty(analysis);
  }

  isDevView(): boolean {
    return !this.featureFlags.isProduction() && !this.featureFlags.isUserView();
  }

  rankedKeywordsDevNoteForAnalysis(analysis: any): string | null {
    if (!this.isDevView()) return null;
    return rankedKeywordsDevDiagnostic(analysis);
  }

  formatEstTraffic(analysis: any): string {
    const v = analysis?.estimatedOrganicTraffic;
    if (!Number.isFinite(v) || v === null || v === undefined) return '—';
    if (v >= 1000) return `${(v / 1000).toFixed(1)}k`;
    return String(Math.round(v));
  }

  getAnalyzedAtIsoForSide(side: ColumnSide): string | null {
    const a = this.analysisForSide(side);
    return a ? getAnalyzedAtIso(a) : null;
  }

  getLastModifiedIsoForSide(side: ColumnSide): string | null {
    const a = this.analysisForSide(side);
    return a ? getLastModifiedIso(a) : null;
  }

  getCriticalIssuesForSide(side: ColumnSide): string[] {
    const a = this.analysisForSide(side);
    if (!a) return [];
    return getCriticalIssues(a);
  }

  getCriticalIssuesCountForSide(side: ColumnSide): number {
    return this.getCriticalIssuesForSide(side).length;
  }

  toggleIssuesDetails(): void {
    this.issuesDetailsExpanded = !this.issuesDetailsExpanded;
  }

  isIssuesDetailsExpanded(): boolean {
    return this.issuesDetailsExpanded;
  }

  toggleKeywordOpportunities(): void {
    this.keywordOpportunitiesExpanded = !this.keywordOpportunitiesExpanded;
  }

  isKeywordOpportunitiesExpanded(): boolean {
    return this.keywordOpportunitiesExpanded;
  }

  toggleKeywordDetails(): void {
    this.keywordDetailsExpanded = !this.keywordDetailsExpanded;
  }

  isKeywordDetailsExpanded(): boolean {
    return this.keywordDetailsExpanded;
  }

  get showKeywordDependencyCallout(): boolean {
    if (!this.bothColumnsCompleted) return false;
    return (
      this.rankedKeywordsNoteVisible(this.primaryColumn.analysis) ||
      this.rankedKeywordsNoteVisible(this.compareColumn.analysis)
    );
  }

  computeKeywordOverlap(): KeywordOverlapAnalysis | null {
    if (!this.bothColumnsCompleted) return null;
    return computeKeywordOverlapFromAnalyses(
      this.primaryColumn.analysis,
      this.compareColumn.analysis
    );
  }

  getKeywordOpportunities(): ContentOpportunity[] {
    return getContentOpportunitiesFromOverlap(this.computeKeywordOverlap());
  }

  getSharedKeywordsWithPositions(): Array<{
    keyword: string;
    yourPosition: number;
    competitorPosition: number;
    searchVolume?: number;
  }> {
    return this.computeKeywordOverlap()?.sharedKeywords || [];
  }

  getPrimaryUniqueKeywords(): Array<{ keyword: string; position: number; searchVolume?: number }> {
    return this.computeKeywordOverlap()?.yourUniqueKeywords || [];
  }

  getCompareUniqueKeywords(): Array<{ keyword: string; position: number; searchVolume?: number }> {
    return this.computeKeywordOverlap()?.competitorUniqueKeywords || [];
  }

  getDiagnosisBullets(): DiagnosisBullet[] {
    if (!this.bothColumnsCompleted) return [];
    return buildQuickCompareDiagnosisBullets({
      yourAnalysis: this.primaryColumn.analysis,
      competitorAnalysis: this.compareColumn.analysis,
      overlap: this.computeKeywordOverlap(),
      yourIncoming: { contextual: this.getIncomingLinksStats('primary').contextual },
      competitorIncoming: { contextual: this.getIncomingLinksStats('compare').contextual },
      yourTopicFit: this.topicFitForSide('primary'),
      competitorTopicFit: this.topicFitForSide('compare')
    });
  }

  hasCompetitorAdvantagesInDiagnosis(): boolean {
    return diagnosisHasCompetitorWins(this.getDiagnosisBullets());
  }

  hasYourAdvantagesInDiagnosis(): boolean {
    return diagnosisHasYourWins(this.getDiagnosisBullets());
  }

  topicSignalsCount(analysis: any): number {
    return getTopicSignalsCount(analysis);
  }

  /** When both sides have completed analysis, metrics use a synchronized row grid so titles/cards align. */
  get bothColumnsCompleted(): boolean {
    return (
      this.primaryColumn.status === 'completed' &&
      this.compareColumn.status === 'completed' &&
      !!this.primaryColumn.analysis &&
      !!this.compareColumn.analysis
    );
  }

  /** True while a competitor force re-fetch can be started (not already analyzing/loading). */
  get canForceRefetchCompare(): boolean {
    const status = this.compareColumn.status;
    return (
      !!((this.compareUrl || '').trim()) &&
      !!((this.primaryUrl || '').trim()) &&
      status !== 'loading' &&
      status !== 'analyzing'
    );
  }

  /**
   * Force a fresh competitor crawl + Labs ranked keywords, replacing any stored PageAnalysis
   * for that URL. Primary (your) page is left as-is.
   */
  forceRefetchCompare(): void {
    const gen = this.loadRequestGen;
    const primary = (this.primaryUrl || '').trim();
    const compare = (this.compareUrl || '').trim();
    if (!primary || !compare || !this.canForceRefetchCompare) return;

    this.supportingPages = { status: 'idle', topics: [], expanded: false };
    this.sectionGap = this.initialSectionGap();
    this.setColumn(
      'compare',
      { status: 'analyzing', analysis: null, error: null, progress: null },
      gen
    );

    const postSub = this.pageComparison
      .comparePages(primary, compare, { forceRefreshCompetitor: true })
      .subscribe({
        next: (comparisonResp) => {
          if (gen !== this.loadRequestGen) return;
          // Keep primary from the response if present; never force-refresh it here.
          if (comparisonResp.data.yourPage?.status === 'completed' && comparisonResp.data.yourPage.analysis) {
            this.applyCompareResult(comparisonResp.data.yourPage, 'primary', gen);
          }
          this.applyCompareResult(comparisonResp.data.competitorPage, 'compare', gen);
          const comp = comparisonResp.data.competitorPage;
          if (comp.status === 'pending' && comp.taskId) {
            this.startPoll(comp.taskId, 'compare', gen);
          }
        },
        error: (err: HttpErrorResponse) => {
          if (gen !== this.loadRequestGen) return;
          this.setColumn(
            'compare',
            {
              status: 'error',
              analysis: null,
              error: this.formatHttpError(err),
              progress: null
            },
            gen
          );
        }
      });
    this.loadSubscription.add(postSub);
  }

  analysisForSide(side: ColumnSide): any {
    return side === 'primary' ? this.primaryColumn.analysis : this.compareColumn.analysis;
  }

  /**
   * Links this page points OUT to — the half of the internal-links story that
   * survives against a rival whose domain we have not crawled (§4b).
   *
   * It is read off the page's own analysis, so it is available for any URL we
   * have ever fetched, including a single-page-crawled SERP winner. The
   * incoming card beside this one counts links found in our crawl of *their*
   * domain and goes to an em dash there. Two cards, two different questions,
   * and the labels have to say which is which — "internal links" alone was
   * what let the two be read as one number.
   */
  getOutgoingLinkStats(side: ColumnSide): {
    total: number;
    contextual: number;
    structural: number;
    samePage: number;
    unknown: number;
  } {
    return getLinkStatsFromAnalysis(this.analysisForSide(side));
  }

  /** True when we hold no classified link data for this side at all. */
  outgoingLinksUnmeasured(side: ColumnSide): boolean {
    const analysis = this.analysisForSide(side);
    return !analysis || !Array.isArray(analysis?.links?.internalLinks);
  }

  topicFitForSide(side: ColumnSide): QuickCompareTopicFitData {
    const col = side === 'primary' ? this.primaryColumn : this.compareColumn;
    const url = (side === 'primary' ? this.primaryUrl : this.compareUrl) || '';
    if (col.status !== 'completed' || !col.analysis) {
      return emptyTopicFitData();
    }
    const mainTopic = getMainTopicFromAnalysis(col.analysis);
    const domain = extractDomainFromUrl(url.trim());
    const normalizedDomain = domain.toLowerCase().trim();
    if (!normalizedDomain) {
      return emptyTopicFitData();
    }
    const loading = this.domainTopicsLoading.get(normalizedDomain) || false;
    const error = this.domainTopicsError.get(normalizedDomain) ?? null;
    const hasKey = this.domainTopicsCache.has(normalizedDomain);
    const parentTopics = hasKey ? this.domainTopicsCache.get(normalizedDomain) : undefined;
    return computeTopicFitData({
      mainTopic,
      parentTopics,
      domainTopicsLoading: loading,
      domainTopicsError: error
    });
  }

  formatTopicTierPublic(tier: QuickCompareTopicFitData['tier']): string {
    return formatTopicTier(tier);
  }

  /** The tier chip's `.badge` classes. Tone map lives with the tier type. */
  topicTierBadgeClasses(tier: QuickCompareTopicFitData['tier']): string {
    return topicTierBadgeClass(tier);
  }

  topicFitSupportingPagesInsight(side: ColumnSide): { show: boolean; topicLabel: string } {
    if (side !== 'primary') return { show: false, topicLabel: '' };
    const topics: TopicFitSupportingPagesTopic[] = (this.supportingPages.topics || []).map((topic) => ({
      topicSlug: topic.topicSlug,
      topicLabel: topic.topicLabel,
      yourPages: topic.yourPages.map((page) => ({ url: page.url })),
      theirPages: topic.theirPages.map((page) => ({ url: page.url }))
    }));
    return buildTopicFitSupportingPagesInsight({
      topicFit: this.topicFitForSide(side),
      supportingTopics: topics,
      focalUrl: (this.primaryUrl || '').trim(),
      side
    });
  }

  linkTypeLabel(t: string | undefined): string {
    return getLinkTypeLabel(t);
  }

  linkTypeTooltip(t: string | undefined): string {
    return getLinkTypeTooltip(t);
  }

  linkTypeBadgeClasses(t: string | undefined): string {
    return linkTypeBadgeClass(t);
  }

  anchorText(t: string | undefined): string {
    return formatAnchorText(t);
  }

  targetUrlForAnalysis(analysis: any): string {
    return linkTargetDisplayUrl(analysis);
  }

  incomingFor(side: ColumnSide): IncomingLinksColumnState {
    return side === 'primary' ? this.primaryIncoming : this.compareIncoming;
  }

  /**
   * True when we hold no page of this domain that could have linked to the
   * target, so incoming links were never measurable.
   *
   * Keyed on link SOURCES, not on pages held. A SERP winner is single-page
   * crawled by `top-serp-rivals.ts`, so its domain usually has exactly one page
   * here — its own — and `totalIncomingLinks` is then 0 by construction rather
   * than by measurement. Counting pages held would see that 1 and report the 0
   * as real, which is the inverse of the finding and DESIGN.md's rule about
   * "passed" and "never ran" not sharing a line.
   *
   * Not `requiresDomainCrawl` either: true at 0 sources and at 4, and at 4 the
   * count is a real floor worth showing under the warning it already has.
   */
  incomingLinksUnmeasured(side: ColumnSide): boolean {
    const meta = this.incomingFor(side).metadata;
    if (!meta) return false;
    if (this.incomingFor(side).links?.length) return false;
    return meta.linkSourcePagesInDomain === 0;
  }

  /** The count, or null when it was never measured — never a stand-in 0. */
  getIncomingLinksCount(side: ColumnSide): number | null {
    const incoming = this.incomingFor(side);
    if (incoming.links !== null) {
      return incoming.links.length;
    }
    if (this.incomingLinksUnmeasured(side)) return null;
    return incoming.metadata?.totalIncomingLinks || 0;
  }

  getIncomingLinksStats(side: ColumnSide): {
    total: number;
    contextual: number;
    structural: number;
    samePage: number;
    unknown: number;
  } {
    const incoming = this.incomingFor(side);
    if (incoming.links === null && incoming.metadata) {
      const meta = incoming.metadata;
      return {
        total: meta.totalIncomingLinks || 0,
        contextual: meta.contextualLinks || 0,
        structural: meta.structuralLinks || 0,
        samePage: meta.samePageLinks || 0,
        unknown: meta.unknownLinks || 0
      };
    }
    const links = incoming.links || [];
    return {
      total: links.length,
      contextual: links.filter((l) => l.linkType === 'contextual').length,
      structural: links.filter((l) => l.linkType === 'structural').length,
      samePage: links.filter((l) => l.linkType === 'same_page').length,
      unknown: links.filter((l) => !l.linkType || l.linkType === 'unknown').length
    };
  }

  getDisplayedIncomingLinks(side: ColumnSide): IncomingLink[] {
    const incoming = this.incomingFor(side);
    const links = incoming.links || [];
    const filtered = links.filter(
      (link) => link.linkType === 'contextual' || link.linkType === 'same_page'
    );
    if (incoming.showAll) {
      return filtered;
    }
    return filtered.slice(0, this.internalLinksDisplayLimit);
  }

  hasMoreIncomingLinks(side: ColumnSide): boolean {
    const incoming = this.incomingFor(side);
    const links = incoming.links || [];
    const filtered = links.filter(
      (link) => link.linkType === 'contextual' || link.linkType === 'same_page'
    );
    return !incoming.showAll && filtered.length > this.internalLinksDisplayLimit;
  }

  shouldShowIncomingLinksWarning(side: ColumnSide): boolean {
    const meta = this.incomingFor(side).metadata;
    if (!meta) return false;
    return meta.requiresDomainCrawl === true;
  }

  toggleIncomingExpanded(side: ColumnSide): void {
    const incoming = this.incomingFor(side);
    incoming.expanded = !incoming.expanded;
    if (incoming.expanded && incoming.links === null && !incoming.fullLoading) {
      this.loadIncomingFull(side);
    }
  }

  isIncomingExpanded(side: ColumnSide): boolean {
    return this.incomingFor(side).expanded;
  }

  toggleShowAllIncomingLinks(side: ColumnSide): void {
    const incoming = this.incomingFor(side);
    incoming.showAll = !incoming.showAll;
  }

  incomingExpandDisabled(side: ColumnSide): boolean {
    return this.incomingFor(side).fullLoading;
  }

  private initialColumn(): ColumnState {
    return {
      status: 'loading',
      analysis: null,
      error: null,
      progress: null
    };
  }

  private initialIncoming(): IncomingLinksColumnState {
    return {
      metadata: null,
      links: null,
      fullLoading: false,
      error: null,
      expanded: false,
      showAll: false
    };
  }

  private resetColumns(): void {
    this.primaryColumn = this.initialColumn();
    this.compareColumn = this.initialColumn();
    this.resetAllEnrichment();
  }

  private resetAllEnrichment(): void {
    this.primaryIncoming = this.initialIncoming();
    this.compareIncoming = this.initialIncoming();
    this.domainTopicsCache.clear();
    this.domainTopicsLoading.clear();
    this.domainTopicsError.clear();
    this.supportingPages = { status: 'idle', topics: [], expanded: false };
    this.sectionGap = this.initialSectionGap();
    this.issuesDetailsExpanded = false;
    this.keywordOpportunitiesExpanded = false;
    this.keywordDetailsExpanded = false;
  }

  private initialSectionGap(): SectionGapState {
    return {
      status: 'idle',
      serviceAvailable: true,
      missingSectionCount: 0,
      rows: [],
      skippedNote: null,
      warningNote: null,
      error: null,
      expanded: false,
      rankDrivenGaps: [],
      cachedEvalRow: null
    };
  }

  private resetColumnEnrichment(side: ColumnSide): void {
    if (side === 'primary') {
      this.primaryIncoming = this.initialIncoming();
    } else {
      this.compareIncoming = this.initialIncoming();
    }
  }

  private teardownLoads(): void {
    this.loadSubscription.unsubscribe();
    this.loadSubscription = new Subscription();
  }

  private startLoad(): void {
    this.teardownLoads();
    const bucket = this.loadSubscription;
    const gen = ++this.loadRequestGen;
    this.resetColumns();

    const primary = (this.primaryUrl || '').trim();
    const compare = (this.compareUrl || '').trim();
    if (!primary || !compare) {
      if (!primary) {
        this.primaryColumn = {
          status: 'error',
          analysis: null,
          error: 'Missing URL for this column.',
          progress: null
        };
      }
      if (!compare) {
        this.compareColumn = {
          status: 'error',
          analysis: null,
          error: 'Missing URL for this column.',
          progress: null
        };
      }
      return;
    }

    const sub = forkJoin({
      primary: this.safeGetAnalysis(primary),
      compare: this.safeGetAnalysis(compare)
    }).subscribe({
      next: ({ primary: pRes, compare: cRes }) => {
        if (gen !== this.loadRequestGen) return;

        // Handle any GET failures before proceeding. 404 = not yet in DB → show
        // a confirm prompt; any other error → hard error state.
        if (!pRes.ok || !cRes.ok) {
          if (!pRes.ok) {
            this.primaryColumn = pRes.error.status === 404
              ? { status: 'prompt', analysis: null, error: null, progress: null }
              : { status: 'error', analysis: null, error: this.formatHttpError(pRes.error), progress: null };
          }
          if (!cRes.ok) {
            this.compareColumn = cRes.error.status === 404
              ? { status: 'prompt', analysis: null, error: null, progress: null }
              : { status: 'error', analysis: null, error: this.formatHttpError(cRes.error), progress: null };
          }
          // Apply success to whichever side did load normally.
          if (pRes.ok) { this.applyGetSuccess('primary', pRes.data); }
          if (cRes.ok) { this.applyGetSuccess('compare', cRes.data); }
          return;
        }

        // Both GETs succeeded — TypeScript now knows both are ok: true.
        const pNeed = this.needsRefreshFromGet(pRes.data);
        const cNeed = this.needsRefreshFromGet(cRes.data);

        // Opening the drawer never spends. It used to: any side missing an
        // analysis OR missing Labs ranked keywords fell straight through to
        // `runComparePost`, which crawls both pages and fetches keywords
        // (~$0.01-0.05). That fired on merely *looking* at a comparison, and
        // the `prompt` state below already existed for the 404 case — it was
        // simply skipped whenever the URL happened to be in the database
        // without keywords. Every path that needs new data now stops at the
        // button and waits to be asked.
        if (pNeed) {
          this.setColumn('primary', this.promptColumn(pRes.data), gen);
        } else {
          this.applyGetSuccess('primary', pRes.data);
        }
        if (cNeed) {
          this.setColumn('compare', this.promptColumn(cRes.data), gen);
        } else {
          this.applyGetSuccess('compare', cRes.data);
        }
      },
      error: () => {
        if (gen !== this.loadRequestGen) return;
        this.primaryColumn = {
          status: 'error',
          analysis: null,
          error: 'Could not load comparison.',
          progress: null
        };
        this.compareColumn = {
          status: 'error',
          analysis: null,
          error: 'Could not load comparison.',
          progress: null
        };
      }
    });

    bucket.add(sub);
  }

  /**
   * User confirmed they want to analyze a URL that wasn't yet in the database.
   * Both sides must either be completed/fresh or confirmed before the POST runs.
   */
  confirmAnalysis(side: ColumnSide): void {
    const gen = this.loadRequestGen;
    const primary = (this.primaryUrl || '').trim();
    const compare = (this.compareUrl || '').trim();
    if (!primary || !compare) return;

    // Mark the confirmed side as loading so the spinner shows.
    this.setColumn(side, { status: 'loading', analysis: null, error: null, progress: null }, gen);

    // Only fire the POST once both sides are ready to proceed (not in prompt state).
    const pStatus = this.primaryColumn.status;
    const cStatus = this.compareColumn.status;
    const otherReady = side === 'primary'
      ? cStatus !== 'prompt'
      : pStatus !== 'prompt';

    if (otherReady) {
      this.runComparePost(primary, compare, gen);
    }
    // If the other side is still in prompt state, the POST will fire when they confirm theirs.
  }

  private runComparePost(primary: string, compare: string, gen: number): void {
    const postSub = this.pageComparison.comparePages(primary, compare).subscribe({
        next: (comparisonResp) => {
          if (gen !== this.loadRequestGen) return;
          this.applyCompareResult(comparisonResp.data.yourPage, 'primary', gen);
          this.applyCompareResult(comparisonResp.data.competitorPage, 'compare', gen);
          const y = comparisonResp.data.yourPage;
          const comp = comparisonResp.data.competitorPage;
          if (y.status === 'pending' && y.taskId) {
            this.startPoll(y.taskId, 'primary', gen);
          }
          if (comp.status === 'pending' && comp.taskId) {
            this.startPoll(comp.taskId, 'compare', gen);
          }
        },
        error: (err: HttpErrorResponse) => {
          if (gen !== this.loadRequestGen) return;
          const msg = this.formatHttpError(err);
          this.setColumn('primary', { status: 'error', analysis: null, error: msg, progress: null }, gen);
          this.setColumn('compare', { status: 'error', analysis: null, error: msg, progress: null }, gen);
        }
      });
    this.loadSubscription.add(postSub);
  }

  private safeGetAnalysis(
    url: string
  ): Observable<
    | { ok: true; data: UrlAnalysisResponse }
    | { ok: false; error: HttpErrorResponse }
  > {
    return this.dataForSeo.getUrlAnalysis(url).pipe(
      map((data) => ({ ok: true as const, data })),
      catchError((error: HttpErrorResponse) => of({ ok: false as const, error }))
    );
  }

  /** A `prompt` column that knows which of the two situations it is in. */
  private promptColumn(res: UrlAnalysisResponse): ColumnState {
    const d = res.data;
    const hasAnalysis = d.status === 'completed' && !!d.analysis && this.isAnalysisFresh(d.analysis);
    return {
      status: 'prompt',
      analysis: null,
      error: null,
      progress: null,
      promptReason: hasAnalysis ? 'no_keywords' : 'not_analyzed'
    };
  }

  promptReasonFor(side: ColumnSide): 'not_analyzed' | 'no_keywords' {
    const col = side === 'primary' ? this.primaryColumn : this.compareColumn;
    return col.promptReason ?? 'not_analyzed';
  }

  private needsRefreshFromGet(res: UrlAnalysisResponse): boolean {
    const d = res.data;
    if (d.status !== 'completed' || !d.analysis) return true;
    if (!this.isAnalysisFresh(d.analysis)) return true;
    // On-page analysis can exist without Labs ranked keywords (e.g. prior QC crawl).
    return rankedKeywordsNeverFetched(d.analysis);
  }

  /** True when stored PageAnalysis exists (any age — crawl/post-compare data is reused). */
  private isAnalysisFresh(analysis: any): boolean {
    const at = analysis?.analyzedAt;
    if (!at) return false;
    const analyzed = new Date(at);
    return !Number.isNaN(analyzed.getTime());
  }

  private applyGetSuccess(side: ColumnSide, res: UrlAnalysisResponse): void {
    const d = res.data;
    if (d.status === 'completed' && d.analysis) {
      this.setColumn(
        side,
        {
          status: 'completed',
          analysis: d.analysis,
          error: null,
          progress: null
        },
        this.loadRequestGen
      );
    } else {
      this.setColumn(
        side,
        {
          status: 'empty',
          analysis: null,
          error: null,
          progress: null
        },
        this.loadRequestGen
      );
    }
  }

  private applyCompareResult(result: PageComparisonResult, side: ColumnSide, gen: number): void {
    if (result.status === 'completed' && result.analysis) {
      this.setColumn(
        side,
        {
          status: 'completed',
          analysis: result.analysis,
          error: null,
          progress: null
        },
        gen
      );
    } else if (result.status === 'pending' && result.taskId) {
      this.setColumn(
        side,
        {
          status: 'analyzing',
          analysis: null,
          error: null,
          progress: null
        },
        gen
      );
    } else {
      this.setColumn(
        side,
        {
          status: 'error',
          analysis: null,
          error: 'Unexpected response when refreshing analysis.',
          progress: null
        },
        gen
      );
    }
  }

  private setColumn(side: ColumnSide, state: ColumnState, gen: number): void {
    if (side === 'primary') {
      this.primaryColumn = state;
    } else {
      this.compareColumn = state;
    }

    if (state.status === 'completed' && state.analysis) {
      this.startColumnEnrichment(side, state.analysis, gen);
    } else {
      this.resetColumnEnrichment(side);
    }

    if (this.bothColumnsCompleted && this.supportingPages.status === 'idle') {
      this.loadSupportingPages(gen);
    }
    if (this.bothColumnsCompleted && this.sectionGap.status === 'idle') {
      this.loadSectionGap(gen);
    }
  }

  private startColumnEnrichment(side: ColumnSide, analysis: any, gen: number): void {
    this.resetColumnEnrichment(side);
    const urlForSide = ((side === 'primary' ? this.primaryUrl : this.compareUrl) || '').trim();
    const domain = extractDomainFromUrl(urlForSide);

    if (getTopicSignalsCount(analysis) > 0 && domain) {
      this.ensureDomainTopicsLoaded(domain, gen);
    }

    const urlId = resolveUrlIdFromAnalysis(analysis);
    const incoming = side === 'primary' ? this.primaryIncoming : this.compareIncoming;

    if (!urlId) {
      incoming.error = 'URL ID not available in analysis';
      return;
    }

    const sub = this.pageComparison
      .getIncomingLinks(urlId, { summaryOnly: true })
      .pipe(
        take(1),
        catchError(() => of(null))
      )
      .subscribe((response) => {
        if (gen !== this.loadRequestGen) return;
        if (response?.success) {
          incoming.metadata = response.data.metadata;
        }
      });
    this.loadSubscription.add(sub);
  }

  private loadSupportingPages(gen: number): void {
    const primaryAnalysis = this.primaryColumn.analysis;
    if (!primaryAnalysis) return;

    const focalUrl = (this.primaryUrl || '').trim();
    const primaryDomain = extractDomainFromUrl(focalUrl);
    const compareDomain = extractDomainFromUrl((this.compareUrl || '').trim());
    if (!primaryDomain || !compareDomain) return;

    this.supportingPages = { status: 'loading', topics: [], expanded: false };

    const parentTopicsFor = (domain: string) =>
      (this.domainTopicsCache.get(domain.toLowerCase().trim()) || []).map((topic) => ({
        parentTopicSlug: topic.parentTopicSlug,
        childTopicSlugs: topic.childTopicSlugs
      }));

    const applyModel = (model: { topics: SupportingTopic[]; source: 'snapshot' | 'live' | 'empty' }) => {
      if (gen !== this.loadRequestGen) return;
      this.supportingPages = {
        status: 'ready',
        topics: model.topics,
        expanded: false,
        source: model.source
      };
    };

    const snapshotSub = this.dataForSeo.getSupportingPagesByTopicSnapshot(focalUrl).pipe(take(1)).subscribe({
      next: (res) => {
        if (gen !== this.loadRequestGen) return;
        if (res.cached && res.data) {
          const sub = loadSupportingPagesContext({
            mode: 'snapshot',
            focalUrl,
            focalAnalysis: primaryAnalysis,
            yourDomain: primaryDomain,
            theirDomain: compareDomain,
            snapshot: res.data
          })
            .pipe(take(1))
            .subscribe((model) => applyModel(model));
          this.loadSubscription.add(sub);
          return;
        }

        this.ensureDomainTopicsLoaded(primaryDomain, gen);
        this.ensureDomainTopicsLoaded(compareDomain, gen);

        const sub = loadSupportingPagesContext({
          mode: 'live',
          focalUrl,
          focalAnalysis: primaryAnalysis,
          yourDomain: primaryDomain,
          theirDomain: compareDomain,
          domainTopicsService: this.domainTopicsService,
          parentTopicsByDomain: {
            your: parentTopicsFor(primaryDomain),
            their: parentTopicsFor(compareDomain)
          }
        })
          .pipe(take(1), catchError(() => of({ topics: [], anchorTopicLabels: [], source: 'empty' as const })))
          .subscribe((model) => applyModel(model));
        this.loadSubscription.add(sub);
      },
      error: () => {
        if (gen !== this.loadRequestGen) return;
        this.supportingPages = { status: 'ready', topics: [], expanded: false, source: 'empty' };
      }
    });
    this.loadSubscription.add(snapshotSub);
  }

  toggleSupportingPages(): void {
    this.supportingPages = { ...this.supportingPages, expanded: !this.supportingPages.expanded };
  }

  formatSupportingTraffic(v: number | undefined): string {
    if (!Number.isFinite(v) || v === undefined || v === null) return '';
    if (v >= 1000) return `~${(v / 1000).toFixed(1)}k/mo`;
    return `~${Math.round(v)}/mo`;
  }

  formatSupportingUrl(url: string): string {
    try {
      const parsed = new URL(url);
      return parsed.pathname || '/';
    } catch {
      return url;
    }
  }

  // --- Section coverage ("what they cover that your page doesn't") ---------

  private loadSectionGap(gen: number): void {
    const focalUrl = (this.primaryUrl || '').trim();
    const competitorUrl = (this.compareUrl || '').trim();
    if (!focalUrl || !competitorUrl) return;

    this.sectionGap = { ...this.initialSectionGap(), status: 'loading' };

    const cacheSub = this.dataForSeo
      .getSerpRivalCoverage(focalUrl)
      .pipe(take(1), catchError(() => of(null)))
      .subscribe((cacheRes) => {
        if (gen !== this.loadRequestGen) return;
        const cov = cacheRes?.cached && cacheRes.data ? cacheRes.data : null;
        if (
          cov &&
          cov.status === 'ready' &&
          cov.evalRow &&
          normalizeUrlForCompare(cov.rivalUrl) === normalizeUrlForCompare(competitorUrl)
        ) {
          this.applySectionGapFromRow(cov.evalRow as SupportingPagesV2RowResult);
          return;
        }
        this.loadSectionGapLive(gen, focalUrl, competitorUrl);
      });
    this.loadSubscription.add(cacheSub);
  }

  /** Hydrate section-gap UI from a precomputed eval row (Top SERP rivals snapshot). */
  private applySectionGapFromRow(row: SupportingPagesV2RowResult): void {
    if (row.error) {
      this.sectionGap = {
        ...this.initialSectionGap(),
        status: 'error',
        error: this.formatSectionGapError(row.error)
      };
      return;
    }
    const rows = (row.sectionCoverageGroups?.length ? row.sectionCoverageGroups : row.sectionCoverage) || [];
    this.sectionGap = {
      status: 'ready',
      serviceAvailable: true,
      missingSectionCount: Number.isFinite(row.missingSectionCount) ? row.missingSectionCount : 0,
      rows,
      skippedNote: row.focalGapSkipped
        ? 'One of the pages had too little body text to compare sections.'
        : null,
      warningNote: row.sectionCoverageWarning || null,
      error: null,
      expanded: false,
      rankDrivenGaps: row.rankDrivenGaps || [],
      cachedEvalRow: row
    };
  }

  private loadSectionGapLive(gen: number, focalUrl: string, competitorUrl: string): void {
    const sub = this.dataForSeo
      .postSupportingPagesOpportunityEval({
        focalUrl,
        competitorUrls: [competitorUrl],
        yourCandidateUrls: [],
        v1ByCompetitor: []
      })
      .pipe(take(1), catchError(() => of(null)))
      .subscribe((res) => {
        if (gen !== this.loadRequestGen) return;
        if (!res || !res.success || !res.data) {
          this.sectionGap = { ...this.initialSectionGap(), status: 'error', error: 'Could not compute section coverage.' };
          return;
        }
        if (!res.data.serviceAvailable) {
          this.sectionGap = { ...this.initialSectionGap(), status: 'unavailable', serviceAvailable: false };
          return;
        }
        const row: SupportingPagesV2RowResult | undefined = res.data.rows?.[0];
        if (!row) {
          this.sectionGap = { ...this.initialSectionGap(), status: 'ready' };
          return;
        }
        this.applySectionGapFromRow(row);
      });
    this.loadSubscription.add(sub);
  }

  toggleSectionGap(): void {
    this.sectionGap = { ...this.sectionGap, expanded: !this.sectionGap.expanded };
  }

  /** Normalize raw content-gap errors into operator-friendly copy. */
  private formatSectionGapError(message: string): string {
    if (
      message.includes('string_too_long') ||
      message.includes('HTTP 422') ||
      message.includes('too large for section analysis')
    ) {
      return (
        'This page is too large for section analysis (over 120,000 characters). ' +
        'Tool and converter sites often need better body extraction — we truncated where possible.'
      );
    }
    if (message.startsWith('Content gap analyzer HTTP')) {
      return 'Section analysis is temporarily unavailable. Try again in a moment.';
    }
    return message;
  }

  /** Competitor sections that are not covered on your page (the actionable gaps). */
  /**
   * The rival's sections this page is missing.
   *
   * `sectionGapsForV2Row`'s rule, shared with the Dashboard peek, the review
   * cards and the Supporting pages chips — this panel used to apply a third,
   * private one. It differed in both directions: it never dropped
   * `listicleSuppressed` groups, so a listicle listed one "missing section" per
   * tool the rival mentions, and it dropped every covered section, so a section
   * this page addresses without a single one of its salient phrases did not
   * count — a real gap, just a thinner one.
   *
   * Three definitions of "a missing section" across one product is two too
   * many. This is the one that is written down.
   */
  sectionGapRows(): SectionCoverageRow[] {
    return sectionGaps(this.sectionGap.rows);
  }

  sectionGapRankDrivenGaps(): RankDrivenSectionGap[] {
    const row = this.sectionGap.cachedEvalRow;
    const mergedRow = row
      ? {
          ...row,
          rankDrivenGaps: this.sectionGap.rankDrivenGaps?.length
            ? this.sectionGap.rankDrivenGaps
            : row.rankDrivenGaps,
          sectionCoverage: row.sectionCoverage?.length
            ? row.sectionCoverage
            : this.sectionGap.rows
        }
      : this.sectionGap.rankDrivenGaps?.length
        ? {
            rankDrivenGaps: this.sectionGap.rankDrivenGaps,
            sectionCoverage: this.sectionGap.rows
          }
        : null;

    return rankDrivenGapsForV2Row(mergedRow, {
      focalQuery: this.inferFocalQueryForRankDriven(),
      focalAnalysis: this.primaryColumn.analysis,
      rivalAnalysis: this.compareColumn.analysis
    });
  }

  private inferFocalQueryForRankDriven(): string | undefined {
    const meta = this.primaryColumn.analysis?.rawData?.pageData?.meta;
    const h1 = meta?.htags?.h1?.[0];
    const title = meta?.title;
    const candidate = String(h1 || title || '').trim();
    return candidate || undefined;
  }

  /**
   * findings §16 — the panel's headline and next step when no section is missing.
   * Driven off the counts the panel actually renders, not the stored
   * `missingSectionCount`, so what it says can never contradict what is on screen.
   */
  sectionGapVerdict(): SectionGapVerdict {
    return buildSectionGapVerdict({
      missingSectionCount: this.sectionGapRows().length,
      rankDrivenGapCount: this.sectionGapRankDrivenGaps().length
    });
  }

  sectionGapRankDrivenPassageSegments(keyword: string, passage: string | null | undefined) {
    return splitPassageByKeywordTokens(keyword, passage || '');
  }

  readonly rankDrivenHeadingTag = rankDrivenHeadingTag;

  sectionGapStatusLabel(): string {
    // The rows this panel renders, not the stored count. The verdict below
    // already switched to these "so what it says can never contradict what is
    // on screen"; this chip was left reading the stored number, so the panel
    // could open on three rows under a chip promising four.
    const n = this.sectionGapRows().length;
    const rd = this.sectionGapRankDrivenGaps().length;
    if (this.sectionGap.status === 'ready') {
      if (n > 0) return `${n} missing section${n === 1 ? '' : 's'}`;
      if (rd > 0) return `${rd} rank-driven gap${rd === 1 ? '' : 's'}`;
      return 'No gaps';
    }
    return '';
  }

  /** Rounded 0–100 similarity of a competitor section to its closest section on your page. */
  // Delegated to `utils/section-gaps`, which the review card reads too. Two
  // surfaces deciding "is there a close match" separately is the shape of bug
  // this file has already produced twice.
  sectionGapClosestPct(sc: SectionCoverageRow): number {
    return sectionGapClosestPct(sc);
  }

  sectionGapHasCloseMatch(sc: SectionCoverageRow): boolean {
    return sectionGapHasCloseMatch(sc);
  }

  sectionGapCompetitorHeading(sc: SectionCoverageRow): string {
    return sc.competitorHeadingPath || sc.competitorHeading || 'Untitled section';
  }

  sectionGapClosestHeading(sc: SectionCoverageRow): string {
    return sectionGapClosestHeading(sc);
  }

  private ensureDomainTopicsLoaded(domain: string, gen: number): void {
    const normalizedDomain = domain.toLowerCase().trim();
    if (!normalizedDomain) return;

    if (this.domainTopicsCache.has(normalizedDomain)) {
      return;
    }
    if (this.domainTopicsLoading.get(normalizedDomain)) {
      return;
    }

    this.domainTopicsLoading.set(normalizedDomain, true);
    this.domainTopicsError.set(normalizedDomain, null);

    const sub = this.pageComparison.getDomainTopics(normalizedDomain).pipe(take(1)).subscribe({
      next: (response) => {
        if (gen !== this.loadRequestGen) return;
        this.domainTopicsLoading.set(normalizedDomain, false);
        if (response?.success && Array.isArray(response.data?.parentTopics)) {
          this.domainTopicsCache.set(normalizedDomain, response.data.parentTopics);
        } else {
          this.domainTopicsCache.set(normalizedDomain, []);
        }
      },
      error: (error: any) => {
        if (gen !== this.loadRequestGen) return;
        this.domainTopicsLoading.set(normalizedDomain, false);
        if (error.status === 404) {
          this.domainTopicsCache.set(normalizedDomain, []);
          this.domainTopicsError.set(normalizedDomain, null);
        } else {
          this.domainTopicsError.set(
            normalizedDomain,
            error.error?.message || error.message || 'Failed to load domain topics'
          );
        }
      }
    });
    this.loadSubscription.add(sub);
  }

  private loadIncomingFull(side: ColumnSide): void {
    const col = side === 'primary' ? this.primaryColumn : this.compareColumn;
    const incoming = this.incomingFor(side);
    if (col.status !== 'completed' || !col.analysis) return;
    const urlId = resolveUrlIdFromAnalysis(col.analysis);
    if (!urlId) return;
    const gen = this.loadRequestGen;
    incoming.fullLoading = true;
    incoming.error = null;
    const sub = this.pageComparison
      .getIncomingLinks(urlId)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          if (gen !== this.loadRequestGen) return;
          incoming.fullLoading = false;
          if (response?.success) {
            incoming.links = response.data.incomingLinks;
            incoming.metadata = response.data.metadata;
          } else {
            incoming.error = 'Failed to load incoming links';
            incoming.links = [];
          }
        },
        error: (err: HttpErrorResponse) => {
          if (gen !== this.loadRequestGen) return;
          incoming.fullLoading = false;
          incoming.error = err.error?.message || err.message || 'Failed to load incoming links';
          incoming.links = [];
        }
      });
    this.loadSubscription.add(sub);
  }

  private startPoll(taskId: string, side: ColumnSide, gen: number): void {
    const pollSub = timer(0, 5000)
      .pipe(
        switchMap(() => this.pageComparison.getAnalysisStatus(taskId)),
        takeWhile((response: AnalysisStatusResponse) => {
          const status = response.data?.status;
          if (status === 'pending') return true;
          // Premature recovery used to mark in-progress crawls as failed; keep waiting.
          if (status === 'failed' && this.isStillInProgressMessage(response.data?.error)) {
            return true;
          }
          return false;
        }, true),
        catchError((error: HttpErrorResponse) => {
          const msg = this.formatHttpError(error);
          if (this.isStillInProgressMessage(msg)) {
            return of({
              success: true,
              data: { status: 'pending' as const, progress: undefined }
            } as AnalysisStatusResponse);
          }
          this.setColumn(
            side,
            {
              status: 'error',
              analysis: null,
              error: msg,
              progress: null
            },
            gen
          );
          return of(null as AnalysisStatusResponse | null);
        })
      )
      .subscribe((response) => {
        if (!response || gen !== this.loadRequestGen) return;
        const status = response.data?.status;
        if (status === 'pending' || (status === 'failed' && this.isStillInProgressMessage(response.data?.error))) {
          const p = response.data?.progress;
          const col = side === 'primary' ? this.primaryColumn : this.compareColumn;
          const next = {
            ...col,
            status: 'analyzing' as const,
            error: null,
            progress: p
              ? {
                  pagesCrawled: p.pagesCrawled || 0,
                  pagesInQueue: p.pagesInQueue || 0
                }
              : col.progress
          };
          if (side === 'primary') {
            this.primaryColumn = next;
          } else {
            this.compareColumn = next;
          }
          return;
        }
        if (status === 'completed' && response.data?.analysis) {
          this.setColumn(
            side,
            {
              status: 'completed',
              analysis: response.data.analysis,
              error: null,
              progress: null
            },
            gen
          );
        } else if (status === 'failed') {
          this.setColumn(
            side,
            {
              status: 'error',
              analysis: null,
              error: response.data?.error || response.data?.message || 'Analysis failed.',
              progress: null
            },
            gen
          );
        }
      });

    this.loadSubscription.add(pollSub);
  }

  private isStillInProgressMessage(message: string | null | undefined): boolean {
    return typeof message === 'string' && message.toLowerCase().includes('still in progress');
  }

  private formatHttpError(err: HttpErrorResponse): string {
    const msg = err?.error?.message || err?.message;
    return typeof msg === 'string' && msg.trim() ? msg : 'Could not load analysis for this URL.';
  }
}
